import { useQueryClient } from '@tanstack/react-query';

import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import type { CreateSSOImageViewModel, SSOImageRepositoryResponse } from '@/shared/api/generated';
import { getApiV1SsoimageQueryKey } from '@/shared/api/generated/hooks/SSOImageController/useGetApiV1Ssoimage';
import { usePostApiV1Ssoimage } from '@/shared/api/generated/hooks/SSOImageController/usePostApiV1Ssoimage';
import type { LibrarySortBy } from '../store/atoms/libraryFiltersAtom';
import { libraryFiltersAtom } from '../store/atoms/libraryFiltersAtom';
import type { SlideCategory } from '../store/atoms/slideCategoriesAtom';
import { slideCategoriesAtom } from '../store/atoms/slideCategoriesAtom';
import { slideLibraryAtom } from '../store/atoms/slideLibraryAtom';
import { buildBannerCtaGroup } from '../utils/buildBannerCtaGroup';

export type RequiredFieldsToCreateSlide = Required<
  Pick<CreateSSOImageViewModel, 'title' | 'categoryId' | 'dateStart' | 'dateEnd'>
>;

/**
 * Categoria com seus slides já filtrados e ordenados.
 */
export interface CategoryWithSlides {
  readonly category: SlideCategory;
  readonly slides: SSOImageRepositoryResponse[];
}

/**
 * Gera o conteúdo JSON inicial do canvas para um device, já com o banner CTA pré-populado.
 * O JSON é serializado via `group.toObject(['data'])` para garantir que todos os atributos
 * customizados (data.label, data.id, etc.) sejam preservados no blob storage.
 */
function buildInitialCanvasContent(device: 'desktop' | 'mobile'): string {
  const group = buildBannerCtaGroup(device);
  const groupJson = group.toObject(['data'] as never[]) as unknown as Record<string, unknown>;
  return JSON.stringify({ version: '6.0.0', objects: [groupJson], background: '#141414' });
}

/**
 * Hook principal da biblioteca de slides.
 * Agrega categorias, slides e filtros em uma interface unificada.
 *
 * @returns Estado da biblioteca, filtros e ações disponíveis.
 *
 * @example
 * ```tsx
 * const { allSlides, filters, setSearchQuery, applySearch, createSlide } = useSlideLibrary();
 * ```
 */
export function useSlideLibrary() {
  const [categories] = useAtom(slideCategoriesAtom);
  const [slides, setSlides] = useAtom(slideLibraryAtom);
  const [filters, setFilters] = useAtom(libraryFiltersAtom);
  const resetFilters = useResetAtom(libraryFiltersAtom);
  const queryClient = useQueryClient();

  const { mutateAsync: createSsoimage, isPending: isSaving } = usePostApiV1Ssoimage();

  /** Aplica o valor atual de `searchQuery` como `appliedQuery`. */
  const applySearch = useCallback(() => {
    setFilters(prev => ({ ...prev, appliedQuery: prev.searchQuery }));
  }, [setFilters]);

  const setSearchQuery = useCallback(
    (query: string) => {
      setFilters(prev => ({ ...prev, searchQuery: query }));
    },
    [setFilters],
  );

  const setCategoryFilter = useCallback(
    (categoryId: number | null) => {
      setFilters(prev => ({ ...prev, appliedCategoryId: categoryId }));
    },
    [setFilters],
  );

  const setSortBy = useCallback(
    (sortBy: LibrarySortBy) => {
      setFilters(prev => ({ ...prev, sortBy }));
    },
    [setFilters],
  );

  /** Remove um slide da biblioteca pelo id local. */
  const removeSlide = useCallback(
    //chamar função
    (id: number) => {
      setSlides(prev => prev.filter(s => s.id !== id));
    },
    [setSlides],
  );

  /**
   * Atualiza o statusId de um slide no atom local.
   * @param id - Id local do slide (UUID)
   * @param statusId - Novo status (1 = Draft, 2 = Published)
   */
  const updateSlideStatus = useCallback(
    //Chamar função
    (id: number, statusId: 1 | 2) => {
      setSlides(prev => prev.map(s => (s.id === id ? { ...s, statusId } : s)));
    },
    [setSlides],
  );

  /**
   * Cria um novo slide na biblioteca e retorna o objeto criado.
   * @param name - Título do slide
   * @param categoryId - Id da categoria pai
   */
  const createSlide = useCallback(
    async ({
      title,
      categoryId,
      dateStart,
      dateEnd,
    }: RequiredFieldsToCreateSlide): Promise<SSOImageRepositoryResponse> => {
      const response = (await createSsoimage({
        data: {
          title,
          categoryId,
          dateStart,
          dateEnd,
          langs: [
            { cultureInfo: 'pt-BR', content: buildInitialCanvasContent('desktop'), viewPort: 1 },
            { cultureInfo: 'pt-BR', content: buildInitialCanvasContent('mobile'), viewPort: 2 },
            { cultureInfo: 'en-US', content: buildInitialCanvasContent('desktop'), viewPort: 1 },
            { cultureInfo: 'en-US', content: buildInitialCanvasContent('mobile'), viewPort: 2 },
            { cultureInfo: 'es-ES', content: buildInitialCanvasContent('desktop'), viewPort: 1 },
            { cultureInfo: 'es-ES', content: buildInitialCanvasContent('mobile'), viewPort: 2 },
          ],
        },
      })) as { id: number };

      const newSlide: Required<SSOImageRepositoryResponse> = {
        id: response.id,
        categoryId: categoryId,
        title,
        status: 1,
        dateStart: dateStart ?? null,
        dateEnd: dateEnd ?? null,
        ssoImageLangs: null,
      };
      setSlides(prev => [...prev, newSlide]);
      void queryClient.invalidateQueries({ queryKey: getApiV1SsoimageQueryKey() });
      return newSlide;
    },
    [createSsoimage, setSlides, queryClient],
  );

  /** Lista flat de todos os slides filtrados e ordenados. */
  const allSlides = useMemo<SSOImageRepositoryResponse[]>(() => {
    const { appliedQuery, appliedCategoryId, sortBy } = filters;
    const query = appliedQuery.trim().toLowerCase();

    let filtered = appliedCategoryId
      ? slides.filter(s => s.categoryId === appliedCategoryId)
      : slides;

    if (query) {
      filtered = filtered.filter(s => (s.title ?? '').toLowerCase().includes(query));
    }

    if (sortBy === 'title') {
      return [...filtered].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '', 'pt-BR'));
    }
    return [...filtered].sort(
      (a, b) => new Date(b.dateEnd ?? '').getTime() - new Date(a.dateEnd ?? '').getTime(),
    );
  }, [slides, filters]);

  /**
   * Categorias filtradas por `appliedCategoryId`, com seus slides
   * filtrados por `appliedQuery` e ordenados por `sortBy`.
   * @deprecated Usar `allSlides` para o grid flat da biblioteca.
   */
  const categoriesWithSlides = useMemo<CategoryWithSlides[]>(() => {
    const { appliedQuery, appliedCategoryId, sortBy } = filters;
    const query = appliedQuery.trim().toLowerCase();

    const filteredCategories = appliedCategoryId
      ? categories.filter(c => c.id === appliedCategoryId)
      : categories;

    return filteredCategories.map(category => {
      let categorySlides = slides.filter(s => s.categoryId === category.id);

      if (query) {
        categorySlides = categorySlides.filter(s => (s.title ?? '').toLowerCase().includes(query));
      }

      if (sortBy === 'title') {
        categorySlides = [...categorySlides].sort((a, b) =>
          (a.title ?? '').localeCompare(b.title ?? '', 'pt-BR'),
        );
      } else {
        categorySlides = [...categorySlides].sort(
          (a, b) => new Date(b.dateEnd ?? '').getTime() - new Date(a.dateEnd ?? '').getTime(),
        );
      }

      return { category, slides: categorySlides };
    });
  }, [categories, slides, filters]);

  const totalSlides = slides.length;
  const hasResults = allSlides.length > 0;
  const isFiltered = filters.appliedQuery !== '' || filters.appliedCategoryId !== null;

  return {
    allSlides,
    categoriesWithSlides,
    totalSlides,
    hasResults,
    isFiltered,
    filters,
    categories,
    applySearch,
    setSearchQuery,
    setCategoryFilter,
    setSortBy,
    resetFilters,
    createSlide,
    removeSlide,
    updateSlideStatus,
    isSaving,
  } as const;
}
